import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, can, isMasked } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { DetailShell, InfoRow, InfoGrid } from "@/components/DetailShell";
import { BtnPrimary, BtnSecondary, StatusBadge, Empty, Field, Input } from "@/components/primitives";
import PhotoUploader from "@/components/PhotoUploader";
import DocumentUploader from "@/components/DocumentUploader";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "contact", label: "Contact & Emergency" },
  { key: "documents", label: "Documents" },
  { key: "ppe", label: "PPE Issued" },
];

export default function EmployeeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = can(user.role, "manager");
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [ppe, setPpe] = useState([]);

  async function load() {
    try {
      const { data } = await api.get(`/employees/${id}`);
      setEmp(data);
      setForm(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (tab !== "ppe" || !emp) return;
    (async () => {
      try {
        const { data } = await api.get(`/ppe/issuances?employee_code=${encodeURIComponent(emp.employee_code)}`);
        setPpe(data || []);
      } catch (e) { /* silent */ void e; }
    })();
  }, [tab, emp]);

  async function save() {
    try {
      const payload = {
        employee_code: form.employee_code,
        full_name: form.full_name,
        position: form.position,
        department: form.department || "Operations",
        employment_type: form.employment_type || "full_time",
        hire_date: form.hire_date,
        status: form.status || "active",
        salary_zmw: isMasked(form.salary_zmw) ? emp.salary_zmw : Number(form.salary_zmw || 0),
        nrc_number: form.nrc_number || null,
        gender: form.gender || null,
        date_of_birth: form.date_of_birth || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        next_of_kin: form.next_of_kin || null,
        next_of_kin_phone: form.next_of_kin_phone || null,
        next_of_kin_relation: form.next_of_kin_relation || null,
        permit_url: form.permit_url || null,
        id_photo_url: form.id_photo_url || null,
        documents: form.documents || [],
        photo_url: form.photo_url || null,
        project_code: form.project_code || null,
        notes: form.notes || null,
      };
      await api.put(`/employees/${id}`, payload);
      toast.success("Saved");
      setEditMode(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function saveDocsOnly(docs) {
    setForm({ ...form, documents: docs });
    // persist immediately so users don't lose uploads
    try {
      await api.put(`/employees/${id}`, {
        ...emp,
        documents: docs,
        salary_zmw: isMasked(emp.salary_zmw) ? undefined : emp.salary_zmw,
      });
      setEmp({ ...emp, documents: docs });
    } catch (e) { toast.error(formatError(e)); }
  }

  if (loading) return <div className="phizo-label">Loading employee…</div>;
  if (!emp) return <Empty label="Employee not found." />;

  return (
    <DetailShell
      overline="HR / EMPLOYEE"
      title={emp.full_name}
      subtitle={`${emp.damstra_id || emp.employee_code} · ${emp.position}`}
      backTo="/hr"
      testId="employee-detail"
      actions={
        <>
          <StatusBadge status={emp.status} />
          {canEdit && !editMode && (
            <BtnSecondary onClick={() => setEditMode(true)} data-testid="employee-edit-btn">Edit</BtnSecondary>
          )}
          {canEdit && editMode && (
            <>
              <BtnSecondary onClick={() => { setEditMode(false); setForm(emp); }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={save} data-testid="employee-save-btn">Save</BtnPrimary>
            </>
          )}
        </>
      }
    >
      {/* Tab strip */}
      <div className="mb-6 border-b border-[#e4e4e7] flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`employee-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-[#09090b] text-[#09090b]" : "border-transparent text-[#a1a1aa] hover:text-[#52525b]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <InfoGrid cols={2}>
              <InfoRow label="Damstra ID (Mine Ref)" value={emp.damstra_id || "—"} testId="employee-detail-damstra" />
              <InfoRow label="System Code" value={emp.employee_code} />
              <InfoRow label="Position" value={emp.position} />
              <InfoRow label="Department" value={emp.department} />
              <InfoRow label="Employment Type" value={emp.employment_type} />
              <InfoRow label="Hire Date" value={fmtDate(emp.hire_date)} />
              <InfoRow label="Status" value={emp.status} />
              <InfoRow label="Salary" value={typeof emp.salary_zmw === "string" ? emp.salary_zmw : fmtZMW(emp.salary_zmw)} testId="employee-detail-salary" />
              <InfoRow label="NRC" value={emp.nrc_number || "—"} />
              <InfoRow label="TPIN" value={emp.tpin || "—"} />
              <InfoRow label="SSN / NAPSA" value={emp.social_security_number || "—"} />
              <InfoRow label="Gender" value={emp.gender || "—"} />
              <InfoRow label="Date of Birth" value={fmtDate(emp.date_of_birth)} />
              <InfoRow label="Project" value={emp.project_code || "—"} />
            </InfoGrid>
            {emp.notes && (
              <div className="mt-4 phizo-card p-4">
                <div className="phizo-label mb-2">Notes</div>
                <p className="text-sm whitespace-pre-wrap">{emp.notes}</p>
              </div>
            )}
          </div>
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Profile Photo</div>
            {canEdit ? (
              <PhotoUploader
                value={form.photo_url}
                onChange={(url) => { setForm({ ...form, photo_url: url }); }}
                category="employee"
                size={128}
                testId="employee-photo"
              />
            ) : emp.photo_url ? (
              <img src={emp.photo_url} alt={emp.full_name} className="w-32 h-32 object-cover" />
            ) : <div className="text-sm text-[#a1a1aa]">No photo uploaded.</div>}
            {canEdit && (
              <BtnPrimary className="mt-3 w-full" onClick={save} data-testid="employee-save-photo">Save photo</BtnPrimary>
            )}
          </div>
        </div>
      )}

      {/* Contact & Emergency */}
      {tab === "contact" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Contact Details</div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div><span className="phizo-label">Phone:</span> {emp.phone || "—"}</div>
                <div><span className="phizo-label">Email:</span> {emp.email || "—"}</div>
                <div><span className="phizo-label">Address:</span> {emp.address || "—"}</div>
              </div>
            )}
          </div>
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Emergency Contact / Next of Kin</div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Name"><Input value={form.next_of_kin || ""} onChange={(e) => setForm({ ...form, next_of_kin: e.target.value })} data-testid="employee-nok-name" /></Field>
                <Field label="Phone"><Input value={form.next_of_kin_phone || ""} onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })} data-testid="employee-nok-phone" /></Field>
                <Field label="Relation"><Input value={form.next_of_kin_relation || ""} onChange={(e) => setForm({ ...form, next_of_kin_relation: e.target.value })} data-testid="employee-nok-relation" /></Field>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div><span className="phizo-label">Name:</span> {emp.next_of_kin || "—"}</div>
                <div><span className="phizo-label">Phone:</span> {emp.next_of_kin_phone || "—"}</div>
                <div><span className="phizo-label">Relation:</span> {emp.next_of_kin_relation || "—"}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents */}
      {tab === "documents" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Driver / Operator Permit</div>
            {emp.permit_url ? (
              <a href={emp.permit_url} target="_blank" rel="noreferrer" className="text-sm text-[#002FA7] hover:underline mono break-all">{emp.permit_url}</a>
            ) : <div className="text-sm text-[#a1a1aa] mb-2">No permit uploaded.</div>}
            {canEdit && (
              <DocumentUploader
                value={emp.permit_url ? [{ name: "Current permit", url: emp.permit_url, type: "application/pdf" }] : []}
                onChange={(list) => {
                  const last = list[list.length - 1];
                  const url = last ? last.url : null;
                  api.put(`/employees/${id}`, { ...emp, permit_url: url, salary_zmw: isMasked(emp.salary_zmw) ? undefined : emp.salary_zmw })
                    .then(() => { toast.success("Permit updated"); load(); })
                    .catch((e) => toast.error(formatError(e)));
                }}
                category="employee_permit"
                testId="employee-permit"
              />
            )}
          </div>

          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">NRC / ID Photo</div>
            {emp.id_photo_url ? (
              <a href={emp.id_photo_url} target="_blank" rel="noreferrer" className="text-sm text-[#002FA7] hover:underline mono break-all">{emp.id_photo_url}</a>
            ) : <div className="text-sm text-[#a1a1aa] mb-2">No ID photo uploaded.</div>}
            {canEdit && (
              <DocumentUploader
                value={emp.id_photo_url ? [{ name: "Current ID", url: emp.id_photo_url, type: "image/jpeg" }] : []}
                onChange={(list) => {
                  const last = list[list.length - 1];
                  const url = last ? last.url : null;
                  api.put(`/employees/${id}`, { ...emp, id_photo_url: url, salary_zmw: isMasked(emp.salary_zmw) ? undefined : emp.salary_zmw })
                    .then(() => { toast.success("ID photo updated"); load(); })
                    .catch((e) => toast.error(formatError(e)));
                }}
                category="employee_id"
                testId="employee-id"
              />
            )}
          </div>

          <div className="lg:col-span-2 phizo-card p-4">
            <div className="phizo-label mb-3">Additional Documents</div>
            <DocumentUploader
              value={emp.documents || []}
              onChange={saveDocsOnly}
              category="employee_docs"
              testId="employee-docs"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}

      {/* PPE Issued */}
      {tab === "ppe" && (
        <div className="phizo-card">
          <div className="p-4 border-b border-[#e4e4e7]">
            <div className="phizo-label">PPE Issuance History</div>
          </div>
          {ppe.length === 0 ? (
            <div className="p-5"><Empty label="No PPE issuance recorded for this employee." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="phizo-table">
                <thead>
                  <tr><th>Date</th><th>PPE Type</th><th className="text-right">Qty</th><th>Size</th><th>Colour</th><th>Condition</th><th>Supervisor</th><th>Issued By</th></tr>
                </thead>
                <tbody>
                  {ppe.map((p) => (
                    <tr key={p.id} data-testid={`ppe-row-${p.id}`}>
                      <td className="text-sm">{fmtDate(p.date_of_issue)}</td>
                      <td className="font-semibold">{p.ppe_type}</td>
                      <td className="text-right mono">{p.qty_issued}</td>
                      <td className="text-sm">{p.size || "—"}</td>
                      <td className="text-sm">{p.colour || "—"}</td>
                      <td className="text-sm">{p.condition || "—"}</td>
                      <td className="text-sm text-[#52525b]">{p.supervisor_name || "—"}</td>
                      <td className="text-sm text-[#52525b]">{p.issued_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </DetailShell>
  );
}
